import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type {
  RateHawkSearchParams,
  RateHawkSearchResult,
  RateHawkRoomGroup,
  RateHawkRate,
} from "./types";
import { groupRatesByRoom } from "./ratehawk-accommodation/helpers";
import { RATEHAWK_SEARCH_MOCK } from "@/fixtures/ratehawk-search-mock";
import { RATEHAWK_RATES_BY_HID } from "@/fixtures/ratehawk-rates-mock";

/** Set to true to bypass edge functions and use local fixture data. */
const USE_MOCK = import.meta.env.DEV && import.meta.env.VITE_RATEHAWK_MOCK === "true";

interface UseRateHawkStaysReturn {
  // Search step
  searchResults: RateHawkSearchResult[];
  /** Total hotels available from the API (may exceed searchResults.length if capped at 200). */
  totalHotels: number | null;
  isSearching: boolean;
  searchError: string | null;
  searchStays: (params: RateHawkSearchParams) => Promise<void>;
  // Hotelpage step (rates with book_hash)
  roomGroups: RateHawkRoomGroup[];
  selectedHotel: RateHawkSearchResult | null;
  isLoadingRates: boolean;
  ratesError: string | null;
  /**
   * Fetch Hotelpage rates for a specific hotel.
   * Requires the original search params (checkin/checkout/guests/currency/residency)
   * because RateHawk Hotelpage needs them to return accurate availability.
   */
  fetchRates: (
    hotel: RateHawkSearchResult,
    searchParams: RateHawkSearchParams,
  ) => Promise<void>;
  // State management
  clearSearch: () => void;
  clearRates: () => void;
  /** True when running with VITE_RATEHAWK_MOCK=true (no edge function calls). */
  isMockMode: boolean;
}

export function useRateHawkStays(): UseRateHawkStaysReturn {
  const [searchResults, setSearchResults] = useState<RateHawkSearchResult[]>([]);
  const [totalHotels, setTotalHotels] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [roomGroups, setRoomGroups] = useState<RateHawkRoomGroup[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<RateHawkSearchResult | null>(null);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);

  /**
   * Geo search for hotels.
   * In mock mode: returns RATEHAWK_SEARCH_MOCK after a 600ms delay.
   * In production: calls stays-search edge function which merges
   *   SERP (/search/serp/geo/) + Content API (/hotel_content_by_ids).
   */
  const searchStays = useCallback(async (params: RateHawkSearchParams) => {
    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);

    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 600));
      setSearchResults(RATEHAWK_SEARCH_MOCK);
      setTotalHotels(RATEHAWK_SEARCH_MOCK.length);
      setIsSearching(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("stays-search-rh", {
        body: {
          checkin: params.checkin,
          checkout: params.checkout,
          guests: params.guests,
          latitude: params.latitude,
          longitude: params.longitude,
          radius: params.radius,
          currency: params.currency ?? "EUR",
          residency: params.residency ?? "it",
          filter: params.filter ?? {},
        },
      });

      if (error) throw error;

      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      // Edge function returns { hotels: RateHawkSearchResult[], total_hotels: number }
      const hotels: RateHawkSearchResult[] = parsed?.hotels ?? parsed?.data?.hotels ?? [];
      setSearchResults(hotels);
      setTotalHotels(parsed?.total_hotels ?? hotels.length);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Search failed";
      setSearchError(message);
    } finally {
      setIsSearching(false);
    }
  }, []);

  /**
   * Fetch Hotelpage rates (with book_hash) for a specific hotel.
   * In mock mode: returns RATEHAWK_RATES_BY_HID[hotel.hid] after a 400ms delay.
   * In production: calls stays-rates-rh edge function (RateHawk /search/hp/).
   * Rates are grouped by room_name into RateHawkRoomGroup[].
   */
  const fetchRates = useCallback(
    async (hotel: RateHawkSearchResult, searchParams: RateHawkSearchParams) => {
      setIsLoadingRates(true);
      setRatesError(null);
      setRoomGroups([]);
      setSelectedHotel(hotel);

      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 400));
        const rates = RATEHAWK_RATES_BY_HID[hotel.hid] ?? [];
        setRoomGroups(groupRatesByRoom(rates));
        setIsLoadingRates(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("stays-rates-rh", {
          body: {
            id: hotel.id,
            hid: hotel.hid,
            checkin: searchParams.checkin,
            checkout: searchParams.checkout,
            guests: searchParams.guests,
            currency: searchParams.currency ?? "EUR",
            residency: searchParams.residency ?? "it",
          },
        });

        if (error) throw error;

        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        // Edge function returns { data: { hotels: [{ id, hid, rates, metapolicy_struct, metapolicy_extra_info }] } }
        const hotels = parsed?.data?.hotels ?? parsed?.hotels ?? [];
        const firstHotel = hotels[0];
        const rates: RateHawkRate[] = firstHotel?.rates ?? [];
        setRoomGroups(groupRatesByRoom(rates));
        setSelectedHotel((prev) =>
          prev
            ? {
                ...prev,
                metapolicy_struct: firstHotel?.metapolicy_struct ?? null,
                metapolicy_extra_info: firstHotel?.metapolicy_extra_info ?? null,
              }
            : prev,
        );
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to load rates";
        setRatesError(message);
      } finally {
        setIsLoadingRates(false);
      }
    },
    [],
  );

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setTotalHotels(null);
    setIsSearching(false);
    setSearchError(null);
    setRoomGroups([]);
    setSelectedHotel(null);
  }, []);

  const clearRates = useCallback(() => {
    setRoomGroups([]);
    setSelectedHotel(null);
    setIsLoadingRates(false);
    setRatesError(null);
  }, []);

  return {
    isMockMode: USE_MOCK,
    searchResults,
    totalHotels,
    isSearching,
    searchError,
    searchStays,
    roomGroups,
    selectedHotel,
    isLoadingRates,
    ratesError,
    fetchRates,
    clearSearch,
    clearRates,
  };
}
